import { ref, computed, watch, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import { useQrCodeAutoRefresh } from 'dashboard/composables/useQrCodeAutoRefresh';

const STATUS_POLL_INTERVAL_MS = 3000;

// Shared between the Settings-page reconnect panel and the conversation-list banner - both
// need the exact same QR-fetch/poll/confirm flow against an already-persisted Evolution inbox,
// just rendered differently for their surrounding layout.
//
// Every call is inbox-scoped: the Evolution credentials live on the persisted channel and are
// read server side, so agents (who never receive provider_config) can reconnect too.
export function useEvolutionReconnect(inbox) {
  const store = useStore();
  const { t } = useI18n();

  const showQrPanel = ref(false);
  const isPolling = ref(false);
  const publicLinkUrl = ref('');
  // The number mismatch on screen in the dialog (see Whatsapp::EvolutionNumberMismatch).
  const activeMismatch = ref(null);
  const isResolvingMismatch = ref(false);
  // The question someone closed without answering - not pushed on them again, the banner offers it.
  const closedMismatchExpiresAt = ref(null);
  let statusPollTimer = null;

  // A switch waiting for a decision that nobody here was watching the QR for - scanned from the
  // public link, or picked up by the webhook - still reaches the dashboard through the inbox.
  const pendingMismatch = computed(() => {
    const mismatch = inbox.value.evolution_number_mismatch;
    return mismatch?.status === 'pending' ? mismatch : null;
  });

  const {
    qrcodeBase64,
    isLoadingQrcode,
    secondsRemaining,
    needsManualRefresh,
    totalSeconds,
    start: startQrAutoRefresh,
    stop: stopQrAutoRefresh,
    manualRefresh: refreshQrcode,
  } = useQrCodeAutoRefresh(async () => {
    const { data } = await InboxesAPI.evolutionQrcode(inbox.value.id);
    return data.qrcode_base64;
  });

  const stopStatusPolling = () => {
    if (statusPollTimer) {
      clearInterval(statusPollTimer);
      statusPollTimer = null;
    }
    isPolling.value = false;
  };

  // A refused scan was already unpaired server side, so the QR keeps refreshing for the right
  // phone behind the dialog; seeing it is acknowledging it. A switch that needs a decision holds
  // the session open on the other phone, so the QR flow stops until someone decides.
  const handleMismatch = async mismatch => {
    if (mismatch.status === 'rejected') {
      if (activeMismatch.value?.phone_number === mismatch.phone_number) return;
      activeMismatch.value = mismatch;
      await InboxesAPI.dismissEvolutionNumberChange(inbox.value.id);
      return;
    }

    stopStatusPolling();
    stopQrAutoRefresh();
    showQrPanel.value = false;
    activeMismatch.value = mismatch;
    await store.dispatch('inboxes/get');
  };

  const pollStatus = async () => {
    try {
      const { data } = await InboxesAPI.evolutionConnectionStatus(
        inbox.value.id
      );

      if (data.number_mismatch) {
        await handleMismatch(data.number_mismatch);
        return;
      }

      if (data.state === 'open') {
        stopStatusPolling();
        stopQrAutoRefresh();
        showQrPanel.value = false;
        publicLinkUrl.value = '';
        // The status endpoint already cleared the reauthorization flag server side - refetch so
        // the banner and the inbox warning icon drop without waiting for a page reload.
        await store.dispatch('inboxes/get');
        useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.RECONNECT.SUCCESS'));
      }
    } catch (error) {
      // Transient network hiccups shouldn't abort the polling loop.
    }
  };

  // Best-effort - a public link failure shouldn't block the QR flow the user can still scan
  // themselves, it just means there's nothing to hand off to whoever has the phone.
  const generatePublicLink = async () => {
    try {
      const { data } = await InboxesAPI.evolutionPublicLink(inbox.value.id);
      publicLinkUrl.value = data.url;
    } catch (error) {
      publicLinkUrl.value = '';
    }
  };

  const requestAuthorization = async () => {
    showQrPanel.value = true;
    try {
      await startQrAutoRefresh();
      isPolling.value = true;
      statusPollTimer = setInterval(pollStatus, STATUS_POLL_INTERVAL_MS);
      if (!publicLinkUrl.value) generatePublicLink();
    } catch (error) {
      useAlert(
        error.message ||
          t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.API.ERROR_MESSAGE')
      );
      showQrPanel.value = false;
    }
  };

  // The question goes up on screen as soon as the inbox carries it - also after a reload, or when
  // the QR was scanned from the public link - since the phone is dropped if nobody answers in time.
  // When it goes away without an answer from here (timed out, answered on another screen, the phone
  // dropped the session), the dialog closes and says so.
  watch(
    pendingMismatch,
    pending => {
      if (pending) {
        if (
          !activeMismatch.value &&
          pending.expires_at !== closedMismatchExpiresAt.value
        ) {
          activeMismatch.value = pending;
        }
        return;
      }
      if (
        activeMismatch.value?.status === 'pending' &&
        !isResolvingMismatch.value
      ) {
        useAlert(
          t(
            'INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.NO_LONGER_PENDING',
            {
              scanned: `+${activeMismatch.value.phone_number}`,
            }
          )
        );
        activeMismatch.value = null;
      }
    },
    { immediate: true }
  );

  const reviewMismatch = () => {
    activeMismatch.value = pendingMismatch.value;
  };

  const closeMismatch = () => {
    if (activeMismatch.value?.status === 'pending') {
      closedMismatchExpiresAt.value = activeMismatch.value.expires_at;
    }
    activeMismatch.value = null;
  };

  const acceptNumberChange = async () => {
    isResolvingMismatch.value = true;
    try {
      await InboxesAPI.acceptEvolutionNumberChange(inbox.value.id);
      activeMismatch.value = null;
      await store.dispatch('inboxes/get');
      useAlert(
        t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NUMBER_MISMATCH.CHANGE_SUCCESS')
      );
    } catch (error) {
      useAlert(
        error.response?.data?.message ||
          error.response?.data?.error ||
          t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.API.ERROR_MESSAGE')
      );
      // Most likely the question is gone (timed out, or answered elsewhere) - show where things stand.
      activeMismatch.value = null;
      await store.dispatch('inboxes/get');
    } finally {
      isResolvingMismatch.value = false;
    }
  };

  // Unpairs the other phone and goes straight back to the QR for the inbox's own.
  const declineNumberChange = async () => {
    isResolvingMismatch.value = true;
    try {
      await InboxesAPI.dismissEvolutionNumberChange(inbox.value.id);
      activeMismatch.value = null;
      await store.dispatch('inboxes/get');
      await requestAuthorization();
    } catch (error) {
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.API.ERROR_MESSAGE'));
    } finally {
      isResolvingMismatch.value = false;
    }
  };

  onUnmounted(() => {
    stopStatusPolling();
  });

  return {
    showQrPanel,
    isLoadingQrcode,
    isPolling,
    qrcodeBase64,
    publicLinkUrl,
    secondsRemaining,
    needsManualRefresh,
    totalSeconds,
    requestAuthorization,
    refreshQrcode,
    activeMismatch,
    pendingMismatch,
    isResolvingMismatch,
    reviewMismatch,
    closeMismatch,
    acceptNumberChange,
    declineNumberChange,
  };
}
